/** 证书信息展示顺序（对齐 JIT-AuthenDemo 结果页） */
export const JIT_CERT_ATTR_ORDER = [
  '_saml_pki_cert_subject',
  'X509Certificate.SerialNumber',
  'X509Certificate.NotAfter',
  'X509Certificate.NotBefore',
  'X509Certificate.IssuerDN',
  'X509Certificate.SubjectDN',
] as const

/** UMS 信息常见字段（对齐 JIT-AuthenDemo 结果页） */
export const JIT_UMS_ATTR_ORDER = ['部门', '上级机构', '机构字典', '职务', '身份证'] as const

export interface JitAuthDisplayEntry {
  key: string
  label: string
  value: string
}

function normalizeAttrValue(value: unknown): string {
  if (value == null) return '—'
  const text = String(value).trim()
  return text || '—'
}

/** 按优先顺序生成展示条目，其余字段追加在后 */
export function buildJitAuthDisplayEntries(
  attributes: Record<string, string> | undefined,
  preferredOrder: readonly string[],
): JitAuthDisplayEntry[] {
  if (!attributes || !Object.keys(attributes).length) return []
  const entries: JitAuthDisplayEntry[] = []
  const used = new Set<string>()
  for (const key of preferredOrder) {
    if (!(key in attributes)) continue
    entries.push({ key, label: key, value: normalizeAttrValue(attributes[key]) })
    used.add(key)
  }
  for (const [key, value] of Object.entries(attributes)) {
    if (used.has(key)) continue
    entries.push({ key, label: key, value: normalizeAttrValue(value) })
  }
  return entries
}

export function buildJitCertDisplayEntries(
  attributes?: Record<string, string>,
): JitAuthDisplayEntry[] {
  return buildJitAuthDisplayEntries(attributes, JIT_CERT_ATTR_ORDER)
}

export function buildJitUmsDisplayEntries(
  attributes?: Record<string, string>,
): JitAuthDisplayEntry[] {
  return buildJitAuthDisplayEntries(attributes, JIT_UMS_ATTR_ORDER)
}

const CERT_FIELD_LABELS: Record<string, string> = {
  _saml_pki_cert_subject: '证书主体',
  'X509Certificate.SubjectDN': '证书主体',
  'X509Certificate.SerialNumber': '序列号',
  'X509Certificate.NotBefore': '有效期起',
  'X509Certificate.NotAfter': '有效期止',
  'X509Certificate.IssuerDN': '颁发者',
}

/** 技术字段名 → 中文展示名 */
export function formatJitAuthFieldLabel(key: string): string {
  return CERT_FIELD_LABELS[key] ?? key
}

/** 从 DN 提取 CN 展示 */
export function formatJitCertSubject(dn: string): string {
  const text = dn.trim()
  if (!text) return '—'
  const match = text.match(/CN=([^,]+)/i)
  if (!match?.[1]) return text
  return match[1].trim().replace(/_\d+$/, '')
}

/** 已在摘要区展示的证书字段，详情折叠中不再重复 */
export const JIT_CERT_SUMMARY_KEYS = new Set([
  '_saml_pki_cert_subject',
  'X509Certificate.SubjectDN',
  'X509Certificate.SerialNumber',
])

export function buildJitExtraCertDisplayEntries(
  attributes?: Record<string, string>,
): JitAuthDisplayEntry[] {
  return buildJitCertDisplayEntries(attributes)
    .filter((item) => !JIT_CERT_SUMMARY_KEYS.has(item.key))
    .map((item) => ({
      ...item,
      label: formatJitAuthFieldLabel(item.key),
    }))
}
